/**
 * @module root/shared
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */
// core
import {
  Component,
  OnInit,
  ViewEncapsulation,
  OnDestroy,
  Input,
  AfterViewInit,
} from '@angular/core';
import { Subject } from 'rxjs';
import { Wove } from 'aspect.js/';
// models
import { Footer } from '@root/shared/models/footer.interface';
import { FooterLink, SocialMediaLinks } from '@root/shared/models/footer-link.interface';
// services
import { NavbarService } from '@root/shared/services/navbar.service';
import { CommonUtilService } from '@support/shared/services/common-util.service';
// Constants
import { SHARED_CONSTANTS } from '@root/shared/constants/shared-constants.constants';
import { APIError, ApiProperties } from '@support/api/common/error.interface';
import { HttpEventInterceptorService } from '@core/error-handler/services/http-event-interceptor.service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { takeUntil } from 'rxjs/operators';
import { PDLBaseComponent } from '@experience/reb-pdl/components/pdl.base.component';
import { PdlService } from '@experience/reb-pdl/services/pdl.service';
import { ActivatedRoute } from '@angular/router';
import { FeatureToggleUtilService } from '@support/shared/services/feature-toggle-util.service';

@Wove({ moduleName: SHARED_CONSTANTS.FOOTER_COMPONENT })
/**
 * Component for the UI footer.
 * @access protected - used by the Root module
 */
@Component({
  selector: 'tmo-footer',
  encapsulation: ViewEncapsulation.None, // allows for style sheet to insert global styles
  templateUrl: './footer.component.html',
  styleUrls: ['../../../../../style-sheets/theme/footer.scss'],
})
export class FooterComponent extends PDLBaseComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() public footerData: Footer;
  public footerLinks: Footer;
  /**
   * Unsubscriber for all subscriptions
   */
  public ngUnsubscribe: Subject<void>;
  /**
   * Holds interaction Id
   */
  public interactionId: string;
  /**
   * Holds Activity Id
   */
  public activityIdList: ApiProperties[];
  /**
   * Holds cart Id
   */
  public cartId: string;
  public eventData: object;
  public isDigitalFooterEnabled: boolean;
  public isDesktop: boolean;
  // private isWebChannel: boolean;
  constructor(
    private navbarService: NavbarService,
    private stateStoreUtilsService: StateStoreUtilsService,
    private httpEventInterceptorService: HttpEventInterceptorService,
    private commonUtilService: CommonUtilService,
    public routeSnapshot: ActivatedRoute,
    public pdlService: PdlService,
    private featureToggleUtilService: FeatureToggleUtilService
  ) {
    super(pdlService, routeSnapshot);
    this.ngUnsubscribe = new Subject<void>();
    this.isDigitalFooterEnabled = this.featureToggleUtilService.isNewHeaderFooterEnabled();
  }

  /**
   * Load the initial configuration of footer
   * @returns void
   */
  public ngOnInit(): void {
    this.setFooterLinks();
    this.navbarService.navbarChangeEmitter
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe((event: { id: string }) => {
        this.setFooterLinks();
      });
    // this.isWebChannel = SETTINGS.CHANNEL.toLocaleLowerCase() === 'web';
  }
  /**
   * To redirect user on click of any footer link.
   * @param url - String url of the new route
   * @param isNewTab - boolean to open in new tab or not
   * @returns boolean
   */
  public onClick(url: string, isNewTab: boolean, name?: string): boolean {
    this.triggerClickEvent(url, isNewTab, name);
    if (url.includes('.t-mobile.com/dns')) {
      this.submitDNS(url);
    } else {
      this.commonUtilService.routeToGivenUrl(url, isNewTab);
    }
    return false;
  }

  /**
   * Method to destroy service data.
   * @returns void
   */
  public ngOnDestroy(): void {
    super.ngOnDestroy();
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Sets the value of the footer Link after filtering the aem content
   * @returns void
   */
  private setFooterLinks(): void {
    this.footerLinks = { ...this.footerData };
    this.footerLinks.leftLinks = <FooterLink[]>(
      this.navbarService.getFilteredLinks(this.footerLinks.leftLinks)
    );
    this.footerLinks.secondaryLinks = <FooterLink[]>(
      this.navbarService.getFilteredLinks(this.footerLinks.secondaryLinks)
    );
    this.footerLinks.socialMediaLinks = <SocialMediaLinks[]>(
      this.navbarService.getFilteredLinks(this.footerLinks.socialMediaLinks)
    );
    this.footerLinks.smallLinks = <FooterLink[]>(
      this.navbarService.getFilteredLinks(this.footerLinks.smallLinks)
    );
  }
  public ngAfterViewInit(): void {
    const timeOut = 500;
    setTimeout(() => {
      this.interactionId = this.stateStoreUtilsService.getInteractionId();
      this.activityIdList = [];
      this.httpEventInterceptorService.listenHttpErrors().subscribe((apiError: APIError) => {
        this.cartId = this.stateStoreUtilsService.getCartId();
        this.activityIdList.push({
          endUrl: apiError.properties.endUrl,
          activityId: apiError.properties.activityId,
        });
        this.interactionId = this.interactionId
          ? this.interactionId
          : this.stateStoreUtilsService.getInteractionId();
      });
    }, timeOut);
  }

  /**
   * To get query string url as object
   * @param {string} url
   * @return {string}
   */
  public submitDNS(url: string): void {
    const redirectDNS = document.createElement('form');
    redirectDNS.style.display = 'none';
    redirectDNS.target = 'myWindow';
    redirectDNS.method = 'post';
    redirectDNS.action = url;
    const dnsInput = [];
    let isDNS: boolean = false;
    if (this.getCookie('dnt') === '1') {
      isDNS = true;
    }
    const dnsSettingValue = isDNS ? 'DoNotSell' : 'Sell';
    const dnsSettingInput = this.createDNSInput('LocaldoNotSellSetting', dnsSettingValue);
    dnsInput.push(dnsSettingInput);
    const dnsBrandInput = this.createDNSInput('Brand', 'Magenta');
    dnsInput.push(dnsBrandInput);
    const dnsSiteInput = this.createDNSInput('Site', 'Sell_Web');
    dnsInput.push(dnsSiteInput);
    const dnsURLInput = this.createDNSInput('Origin_URL', location.href);
    dnsInput.push(dnsURLInput);
    // TODO CCPA JIRA1592 : Commenting as of now due to design limitation , will be using it later
    /*   if (this.stateStoreUtilsService.getProfileId()){
      const dnsIdInput = this.createDNSInput('idType', 'TMOID');
      dnsInput.push(dnsIdInput);
      const dnsAuthInput = this.createDNSInput('Authorization', this.stateStoreUtilsService.getJWTToken());
      dnsInput.push(dnsAuthInput);
      const dnsProfileIdInput = this.createDNSInput('userID', this.stateStoreUtilsService.getProfileId());
      dnsInput.push(dnsProfileIdInput);
    }*/
    for (let dnsInputCount = 0; dnsInputCount < dnsInput.length; dnsInputCount++) {
      redirectDNS.appendChild(dnsInput[dnsInputCount]);
    }
    document.body.appendChild(redirectDNS);
    redirectDNS.submit();
  }

  /**
   * To create DNS Input
   * @param {string} name
   * @param {string} value
   * @return {Element}
   */
  private createDNSInput(name: string, value: string): Element {
    const dnsInput = document.createElement('input');
    dnsInput.type = 'text';
    dnsInput.name = name;
    dnsInput.value = value;
    return dnsInput;
  }

  /**
   * Function to get the cookie value
   * @param cookieName Stores the name of the cookie
   * @returns string
   */
  private getCookie(cookieName: string): string {
    // tslint:disable-next-line:no-global-variables
    const match = document.cookie.match(new RegExp(`${cookieName}=([^;]+)`));
    if (match) {
      return match[1];
    }
  }

  private triggerClickEvent(url?: string, isNewTab?: boolean, name?: string): void {
    // if (this.isWebChannel) {
    if (!isNewTab) {
      url = window.location.protocol + '//' + window.location.host + url;
    }
    this.eventData = {
      eventInfo: {
        eventAction: 'click',
        eventType: 'navigation',
      },
      eventDescription: {
        navType: 'fNav',
        category: name ? name : '',
        subCategory: '',
        targetUrl: url,
      },
    };
    this.pdlService.triggerCustomEvent(this.pdlPageComponentId, 'click');
    // }
  }
}
